import withLayout from "../../hocs/Layout";
import {useState, useEffect} from "react";
import {Box, Chip, IconButton, Tooltip, Typography} from "@mui/material";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import {getMerchant} from "../../helpers/ReduxHelpers";
import {apiConstants, post} from "../../common/constants";
import {formatCurrency, formatDate} from "../../helpers/UtilityHelper";
import DownloadSharpIcon from '@mui/icons-material/DownloadSharp';
import PageTitle from "../../common/PageTitle";
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography';
import {NAVY, SUBTLE} from '../../styles/foundation/colors';
import {primaryTextSx, secondaryTextSx} from '../orders/list2/cellStyles';

const STATUS_COLORS = {
    GENERATED: {bgcolor: '#fff3e0', color: '#e65100'},
    OPS_APPROVED: {bgcolor: '#e3f2fd', color: '#1565c0'},
    E_INVOICED: {bgcolor: '#e8f5e9', color: '#2e7d32'},
}

function MerchantInvoice() {
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(20);
    const [data, setData] = useState([])
    const [rowCount, setRowCount] = useState(0)
    const {id: merchantId} = getMerchant()

    useEffect(() => {
        setLoading(true);
        const request = {
            merchantRef: merchantId,
            reportStage: 'E_INVOICED',
            skip: page * sizePerPage,
            limit: sizePerPage,
        }
        post(`${apiConstants.BASE_LOCAL}${apiConstants.SEARCH_INVOICE}`, request)
            .then((res) => {
                setLoading(false);
                setData(res?.data?.responseObj)
            })
            .catch((err) => {
                console.error(err)
                setLoading(false);
            })
    }, [sizePerPage, page])

    useEffect(() => {
        const request = {
            merchantRef: merchantId,
            reportStage: 'E_INVOICED',
            skip: 0,
            limit: 10000,
        }
        post(`${apiConstants.BASE_LOCAL}${apiConstants.SEARCH_INVOICE}`, request)
            .then((res) => {
                setRowCount(res?.data?.responseObj.length)
            })
            .catch((err) => console.error(err))
    }, [])

    const columns = [
        {
            field: 'reportFrequency',
            headerAlign: 'left',
            hideSortIcons: true,
            headerName: 'Frequency',
            flex: 0.8,
            minWidth: 110,
            renderCell: ({row}) => (
                <Typography sx={primaryTextSx}>{row.reportFrequency}</Typography>
            ),
        },
        {
            field: 'createdAt',
            headerAlign: 'left',
            hideSortIcons: true,
            headerName: 'Created Date',
            flex: 1,
            minWidth: 130,
            renderCell: ({row}) => (
                <Typography sx={secondaryTextSx}>{formatDate(row?.createdAt)}</Typography>
            ),
        },
        {
            field: 'reportStage',
            headerAlign: 'left',
            hideSortIcons: true,
            headerName: 'Status',
            flex: 0.9,
            minWidth: 130,
            renderCell: ({row}) => {
                const status = row?.reportStage || ''
                const colors = STATUS_COLORS[status] || {bgcolor: '#f5f5f5', color: '#616161'}
                return (
                    <Chip
                        label={status}
                        size='small'
                        sx={{
                            fontFamily: FONT,
                            fontSize: '11px',
                            fontWeight: WEIGHT.semibold,
                            height: 24,
                            ...colors,
                        }}
                    />
                )
            },
        },
        {
            field: 'fromDate',
            headerAlign: 'left',
            hideSortIcons: true,
            headerName: 'From Date',
            flex: 0.9,
            minWidth: 120,
            renderCell: ({row}) => (
                <Typography sx={secondaryTextSx}>{formatDate(row?.fromDate)}</Typography>
            ),
        },
        {
            field: 'toDate',
            headerAlign: 'left',
            hideSortIcons: true,
            headerName: 'To Date',
            flex: 0.9,
            minWidth: 120,
            renderCell: ({row}) => (
                <Typography sx={secondaryTextSx}>{formatDate(row?.toDate)}</Typography>
            ),
        },
        {
            field: 'totalOrders',
            headerAlign: 'right',
            align: 'right',
            hideSortIcons: true,
            headerName: 'Total Orders',
            flex: 0.8,
            minWidth: 110,
            renderCell: ({row}) => (
                <Typography sx={primaryTextSx}>{row?.totalOrders || 0}</Typography>
            ),
        },
        {
            field: 'totalInvoicedAmount',
            headerAlign: 'right',
            align: 'right',
            hideSortIcons: true,
            headerName: 'Invoiced Amount',
            flex: 1,
            minWidth: 140,
            renderCell: ({row}) => (
                <Typography sx={{...primaryTextSx, color: NAVY}}>
                    {formatCurrency(row?.totalInvoicedAmount || 0)}
                </Typography>
            ),
        },
        {
            field: 'action',
            headerAlign: 'left',
            hideSortIcons: true,
            headerName: 'Action',
            flex: 0.8,
            minWidth: 100,
            renderCell: ({row}) => (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                    <Tooltip title='Download Invoice'>
                        <IconButton onClick={() => window.open(row?.s3Link?.signedLink)} color='primary' size='small'>
                            <DownloadSharpIcon fontSize='small' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Download E-Invoice'>
                        <IconButton onClick={() => window.open(row?.uploadedEInvoiceS3Link?.signedLink)} color='success' size='small'>
                            <DownloadSharpIcon fontSize='small' />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ]

    return (
        <div id="main-content" className="container-fluid">
            <PageTitle>Invoices</PageTitle>
            <ProzoDataGrid
                columns={columns}
                hideFooterPagination={false}
                autoHeight={true}
                rows={data || []}
                sizePerPage={sizePerPage}
                setSizePerPage={setSizePerPage}
                setPage={setPage}
                page={page}
                pagination={true}
                filterMode='server'
                rowCount={rowCount}
                getRowId={(row) => row.id}
            />
        </div>
    )
}

export default withLayout(MerchantInvoice);
